import React, { useCallback, useEffect, useRef, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  ModalHead,
  Headline,
  Buttons,
  ModalContent,
  Icon,
  ModalMain,
  EmailSection,
  Text,
  EmailName,
  MessageSection,
  MessageBox,
  Payment,
  Check,
  Input,
  InputValue,
  SelectedEmail,
  InputDiv,
  InputData,
  ButtonSection,
  Label,
  LabelSection,
  AddCCBlock,
  MessageInput,
  InputSection,
  CloseIcon
} from './styles';
import ButtonV2 from '../../button/buttonV2';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { useDispatch } from 'react-redux';
import { getCustomerList } from '../../../services/customerServices';
import { dispatchInvoiceError, sendInvoiceToCustomers } from '../../../services/invoiceService';
import { ROUTES } from '../../../routes/urls';
import { isDev, isEmail, isEmpty } from '../../../helpers/common';
import { captureException } from '../../../services/logService';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { INVOICE_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../Dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { getCompanySettingIntegration } from '../../../services/settingServices';
import { BUTTON_TYPE } from '../../../global/constants';
interface Props {
  onClose: () => void;
  invoiceId: string;
  workspaceId: string;
}

const SendInvoiceModal: React.FC<Props> = (props) => {
  const { invoiceId, workspaceId } = props;
  //Refs
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const recipientRef = useRef<{
    closeDropdown: () => void;
  }>(null);
  const ccRef = useRef<{
    closeDropdown: () => void;
  }>(null);
  //States
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [carbonCopyEmails, setCarbonCopyEmails] = useState<string[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [subjectInput, setSubjectInput] = useState('');
  const [searchRecipient, setSearchRecipient] = useState('');
  const [searchCC, setSearchCC] = useState('');
  const [customerWithEmailList, setCustomerWithEmailList] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCCDropdownOpen, setIsCCDropdownOpen] = useState(false);
  const [showCCField, setShowCCField] = useState(false);
  const [isIncludePDF, setIsIncludePDF] = useState(false);
  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 640;
  //Other variable
  const dispatch = useDispatch();

  const handleTextareaInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      if (textarea.scrollHeight > textarea.clientHeight) {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, []);

  const onChangeRecipientInput = useCallback((value: any) => {
    setSearchRecipient(value);
  }, []);

  const onChangeCCInput = useCallback((value: any) => {
    setSearchCC(value);
  }, []);

  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspaceId)) {
        setLoading(true);
        let customersList = await dispatch(getCustomerList());
        customersList = customersList?.map((x: CustomerDetailsInterface) => {
          return { ...x, label: `${x?.companyName} - ${x?.email}`, value: x?.email };
        });
        setCustomerWithEmailList(customersList);
        const result = await dispatch(getCompanySettingIntegration(workspaceId));
        setPaymentEnabled(result?.stripe_connected);
      }
    } catch (error) {
      captureException(error);
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, workspaceId]);

  useEffect(() => {
    handleTextareaInput();
    loadData();
  }, [handleTextareaInput, loadData]);

  const onClickClose = useCallback(() => {
    props?.onClose();
    setMessageInput('');
    setIsOnlinePayment(false);
    setCustomerWithEmailList([]);
    setPaymentEnabled(false);
  }, [props]);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        if (isEmpty(recipientEmails) || recipientEmails.length === 0) {
          dispatchInvoiceError('Please enter at least one recipient.');
          return;
        }
        const invalidEmail = recipientEmails?.find((email) => !isEmail(email));
        if (invalidEmail) {
          dispatchInvoiceError('Please enter valid email address.');
          return;
        }
        const payload = {
          emails: recipientEmails,
          onlinePayment: isOnlinePayment,
          message: messageInput,
          subject: subjectInput,
          cc: carbonCopyEmails,
          invoiceId: invoiceId,
          isPdf: isIncludePDF,
          isDev: isDev()
        };
        const result = await dispatch(sendInvoiceToCustomers(payload));
        if (result) {
          trackAnalyticActivity(INVOICE_ANALYTICS.SENT);
          onClickClose();
        }
      } catch (error) {
        captureException(error);
        console.log('error', error);
      } finally {
        setLoading(false);
      }
    },
    [
      recipientEmails,
      isOnlinePayment,
      messageInput,
      subjectInput,
      carbonCopyEmails,
      invoiceId,
      isIncludePDF,
      dispatch,
      onClickClose
    ]
  );

  // Handle item selection
  const selectRecipentEmail = useCallback(
    (email: string) => {
      if (!recipientEmails.includes(email)) {
        setRecipientEmails([...recipientEmails, email]);
      } else {
        const updatedRecipient = recipientEmails?.filter((item) => item !== email) || [];
        setRecipientEmails(updatedRecipient);
      }
      setSearchCC('');
      setSearchRecipient('');
    },
    [recipientEmails]
  );

  const selectCarbonEmail = useCallback(
    (email: string) => {
      if (!carbonCopyEmails.includes(email)) {
        setCarbonCopyEmails([...carbonCopyEmails, email]);
      } else {
        const updatedCarbonEmails = carbonCopyEmails?.filter((item) => item !== email) || [];
        setCarbonCopyEmails(updatedCarbonEmails);
      }
      setSearchCC('');
      setSearchRecipient('');
    },
    [carbonCopyEmails]
  );

  // Update handleKeyDown to handle Enter key
  const handleRecipientKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && searchRecipient === '' && recipientEmails.length > 0) {
        setRecipientEmails(recipientEmails.slice(0, -1)); // Remove last email completely
      }

      if (e.key === 'Enter' && searchRecipient.trim() !== '') {
        // Check if searchTerm matches a customer email
        const foundCustomer =
          customerWithEmailList?.find((item) => item.value?.toLowerCase() === searchRecipient?.trim().toLowerCase()) ??
          null;

        if (
          foundCustomer &&
          !recipientEmails.includes(foundCustomer.value) &&
          !carbonCopyEmails.includes(foundCustomer.value)
        ) {
          setRecipientEmails([...recipientEmails, foundCustomer.value]);
          setSearchRecipient('');
          e.preventDefault();
          return;
        } else if (foundCustomer) {
          dispatchInvoiceError('Email is already added in Recipient or CC.');
          return;
        }

        // Add any input as email (even if not valid)
        if (!recipientEmails.includes(searchRecipient.trim())) {
          if (!isEmail(searchRecipient)) {
            dispatchInvoiceError('Please add proper email');
            return;
          }
          setRecipientEmails([...recipientEmails, searchRecipient.trim()]);
          setSearchRecipient('');
          e.preventDefault();
          return;
        }
      }
    },
    [carbonCopyEmails, customerWithEmailList, recipientEmails, searchRecipient]
  );

  // Update handleKeyDown to handle Enter key
  const handleCCKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && searchCC === '' && carbonCopyEmails.length > 0) {
        setCarbonCopyEmails(carbonCopyEmails.slice(0, -1));
      }

      if (e.key === 'Enter' && searchCC.trim() !== '') {
        // Check if searchTerm matches a customer email
        const foundCustomer =
          customerWithEmailList?.find((item) => item.value?.toLowerCase() === searchCC?.trim().toLowerCase()) ?? null;

        if (
          foundCustomer &&
          !recipientEmails.includes(foundCustomer.value) &&
          !carbonCopyEmails.includes(foundCustomer.value)
        ) {
          setCarbonCopyEmails([...carbonCopyEmails, foundCustomer.value]);
          setSearchCC('');
          e.preventDefault();
          return;
        } else if (foundCustomer) {
          dispatchInvoiceError('Email is already added in Recipient or CC.');
        }

        // Add any input as email (even if not valid)
        if (!carbonCopyEmails.includes(searchCC.trim())) {
          if (!isEmail(searchCC)) {
            dispatchInvoiceError('Please add proper email');
            return;
          }
          setCarbonCopyEmails([...carbonCopyEmails, searchCC.trim()]);
          setSearchCC('');
          e.preventDefault();
        }
      }
    },
    [carbonCopyEmails, customerWithEmailList, recipientEmails, searchCC]
  );

  const handleIncludePDF = useCallback(() => {
    setIsIncludePDF(!isIncludePDF);
  }, [isIncludePDF]);

  return (
    <ModalMain>
      <ModalHead>
        <Headline>Send Invoice</Headline>
        <Icon onClick={onClickClose}>
          <SVGIcon
            name='app-close-icon'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
            className='cross-icon'
          />
        </Icon>
      </ModalHead>
      <ModalContent>
        <EmailSection>
          <InputSection>
            <LabelSection>
              <Text>Recipients</Text>
              {!showCCField && <AddCCBlock onClick={() => setShowCCField(true)}>Add CC</AddCCBlock>}
            </LabelSection>
            <EmailName>
              <Dropdown
                ref={recipientRef}
                modalTitle='Recipient'
                isMinWidth={isMinWidth}
                closeOnItemClick={false}
                updateDropdownOpen={(value) => setIsDropdownOpen(value)}
                content={
                  <InputDiv className='input-dropdown' applySmallPadding={recipientEmails.length > 0}>
                    <InputData>
                      {/* Display selected emails */}
                      {recipientEmails.map((email, index) => (
                        <SelectedEmail key={index}>
                          {email}
                          <span onClick={() => setRecipientEmails(recipientEmails.filter((e) => e !== email))}>
                            <CloseIcon>
                              <SVGIcon
                                name='tag-close-icon'
                                width='12'
                                height='12'
                                viewBox='0 0 12 12'
                                className='tag-close-icon'
                              />
                            </CloseIcon>
                          </span>
                        </SelectedEmail>
                      ))}

                      {/* Input for searching and adding emails */}
                      <Input
                        type='text'
                        placeholder={
                          recipientEmails.length === 0 && searchRecipient === '' ? 'Write Person or Email' : ''
                        }
                        onChange={(e) => onChangeRecipientInput(e.target.value)}
                        onKeyDown={handleRecipientKeyDown}
                        value={searchRecipient}
                      />
                    </InputData>

                    <SVGIcon
                      onClickSvg={(e) => {
                        if (!isDropdownOpen) return;
                        e.preventDefault();
                        e.stopPropagation();
                        recipientRef?.current?.closeDropdown();
                      }}
                      name='angleDown-icon'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      className={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                    />
                  </InputDiv>
                }
                trigger='click'>
                <>
                  {customerWithEmailList
                    ?.filter(
                      (item) =>
                        !isEmpty(item?.value) && item.value.toLowerCase().includes(searchRecipient.toLowerCase())
                    )
                    .map((customer) => {
                      const isSelected = recipientEmails.includes(customer.value);
                      return (
                        <DropdownItem
                          isChecked={isSelected}
                          key={customer.value}
                          label={customer.label}
                          onClick={() => selectRecipentEmail(customer.value)}
                          isSuffix={
                            isSelected ? (
                              <SVGIcon name='checkbox-icon' width='16' height='16' viewBox='0 0 16 16' />
                            ) : null
                          }
                        />
                      );
                    })}
                </>
              </Dropdown>
            </EmailName>
          </InputSection>
          {/* cc field */}
          {showCCField && (
            <InputSection>
              <LabelSection>
                <Text>CC</Text>
                <AddCCBlock
                  onClick={() => {
                    setCarbonCopyEmails([]);
                    setSearchCC('');
                    setShowCCField(false);
                  }}>
                  Remove
                </AddCCBlock>
              </LabelSection>
              <EmailName>
                <Dropdown
                  ref={ccRef}
                  modalTitle='Recipient'
                  isMinWidth={isMinWidth}
                  closeOnItemClick={false}
                  updateDropdownOpen={(value) => setIsCCDropdownOpen(value)}
                  content={
                    <InputDiv className='input-dropdown' applySmallPadding={carbonCopyEmails.length > 0}>
                      <InputData>
                        {/* Display selected emails */}
                        {carbonCopyEmails.map((email, index) => (
                          <SelectedEmail key={index}>
                            {email}
                            <span onClick={() => setCarbonCopyEmails(carbonCopyEmails.filter((e) => e !== email))}>
                              <CloseIcon>
                                <SVGIcon
                                  name='tag-close-icon'
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                  className='tag-close-icon'
                                />
                              </CloseIcon>
                            </span>
                          </SelectedEmail>
                        ))}

                        {/* Input for searching and adding emails */}
                        <Input
                          type='text'
                          placeholder={carbonCopyEmails.length === 0 && searchCC === '' ? 'Write Person or Email' : ''}
                          onChange={(e) => onChangeCCInput(e.target.value)}
                          onKeyDown={handleCCKeyDown}
                          value={searchCC}
                        />
                      </InputData>

                      <SVGIcon
                        onClickSvg={(e) => {
                          if (!isCCDropdownOpen) return;
                          e.preventDefault();
                          e.stopPropagation();
                          ccRef?.current?.closeDropdown();
                        }}
                        name='angleDown-icon'
                        width='18'
                        height='18'
                        viewBox='0 0 18 18'
                        className={`angleDown-icon ${isCCDropdownOpen ? 'rotate' : ''}`}
                      />
                    </InputDiv>
                  }
                  trigger='click'>
                  <>
                    {customerWithEmailList
                      ?.filter(
                        (item) => !isEmpty(item?.value) && item.value.toLowerCase().includes(searchCC.toLowerCase())
                      )
                      .map((customer) => {
                        const isSelected = carbonCopyEmails.includes(customer.value);
                        return (
                          <DropdownItem
                            isChecked={isSelected}
                            key={customer.value}
                            label={customer.label}
                            onClick={() => selectCarbonEmail(customer.value)}
                            isSuffix={
                              isSelected ? (
                                <SVGIcon name='checkbox-icon' width='16' height='16' viewBox='0 0 16 16' />
                              ) : null
                            }
                          />
                        );
                      })}
                  </>
                </Dropdown>
              </EmailName>
            </InputSection>
          )}
          {/* message subject field */}
          <MessageSection>
            <Text>Message subject</Text>
            <MessageInput
              placeholder='Enter message subject'
              value={subjectInput}
              onChange={(e) => setSubjectInput(e.target.value)}
            />
          </MessageSection>
          <MessageSection>
            <Text>Message body</Text>
            <MessageBox
              placeholder='Enter your Message'
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}></MessageBox>
          </MessageSection>
          <Payment>
            {paymentEnabled ? (
              <>
                <Check>
                  <InputValue
                    type='checkbox'
                    id='paymentDisable'
                    name='paymentDisable'
                    className='checkbox-round'
                    checked={isOnlinePayment}
                    onChange={(e) => setIsOnlinePayment(e.target.checked)}
                  />
                  <label htmlFor='paymentDisable' className='label-checkbox'>
                    Collect Online Payment
                  </label>
                </Check>
              </>
            ) : (
              <>
                <Check>
                  <p>
                    <a href={ROUTES.PAYMENT_SETTING}>Configure</a> Stripe or Paypal to collect Payment
                  </p>
                </Check>
              </>
            )}
          </Payment>
        </EmailSection>
        <ButtonSection>
          <Check>
            <InputValue type='checkbox' className='checkbox-round' checked={isIncludePDF} onClick={handleIncludePDF} />
            <Label>Include a PDF version of the invoice</Label>
          </Check>
          <Buttons>
            <ButtonV2 title='Cancel' onClick={onClickClose} buttonType='button' variant={BUTTON_TYPE.SECONDARY} />
            <ButtonV2
              title='Send Invoice'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={onSubmit}
              buttonType='button'
              isLoading={loading}
              disabled={loading}
            />
          </Buttons>
        </ButtonSection>
      </ModalContent>
    </ModalMain>
  );
};

export default SendInvoiceModal;
